import { MortgageData } from '@/components/calculator/CalculatorTabs';
import { LongTermProjection, RateStrategyAnalytics } from '@/types/analytics';

export const projectLongTermFinancials = (
  mortgageData: MortgageData,
  appreciationRate: number = 3.5
): LongTermProjection => {
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const monthlyRate = mortgageData.interestRate / 100 / 12;
  const numberOfPayments = mortgageData.loanTerm * 12;
  const monthlyPI = mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest;
  
  const milestoneYears = [5, 10, 15, 20, 25, 30];
  const milestones = milestoneYears
    .filter(years => years <= mortgageData.loanTerm)
    .map(years => {
      const months = years * 12;
      let remainingBalance = loanAmount;
      let totalPrincipalPaid = 0;
      let totalInterestPaid = 0;

      for (let i = 0; i < months; i++) {
        const interestPayment = remainingBalance * monthlyRate;
        const principalPayment = monthlyPI - interestPayment;
        totalInterestPaid += interestPayment;
        totalPrincipalPaid += principalPayment;
        remainingBalance -= principalPayment;
      }

      const estimatedHomeValue = mortgageData.homePrice * Math.pow(1 + appreciationRate / 100, years);
      const homeEquity = estimatedHomeValue - Math.max(0, remainingBalance);
      
      return {
        years,
        totalPaid: totalPrincipalPaid + totalInterestPaid,
        principalPaid: totalPrincipalPaid,
        interestPaid: totalInterestPaid,
        remainingBalance: Math.max(0, remainingBalance),
        homeEquity,
        estimatedHomeValue
      };
    });

  const finalMilestone = milestones[milestones.length - 1];
  const netWorthImpact = finalMilestone ? finalMilestone.homeEquity : 0;

  return {
    milestones,
    appreciationRate,
    netWorthImpact
  };
};

export const calculateRateStrategyAnalytics = (
  mortgageData: MortgageData
): RateStrategyAnalytics => {
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const terms = [15, 20, 30];
  
  const termComparison = terms.map(term => {
    const monthlyRate = mortgageData.interestRate / 100 / 12;
    const numberOfPayments = term * 12;
    const monthlyPayment = loanAmount * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    const totalCost = monthlyPayment * numberOfPayments;
    const totalInterest = totalCost - loanAmount;
    
    const targetBalance = mortgageData.homePrice * 0.8;
    let remainingBalance = loanAmount;
    let months = 0;
    
    while (remainingBalance > targetBalance && months < numberOfPayments) {
      const interestPayment = remainingBalance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      remainingBalance -= principalPayment;
      months++;
    }
    
    const yearsToEquity = months / 12;

    return {
      term,
      monthlyPayment,
      totalInterest,
      totalCost,
      yearsToEquity
    };
  });

  const currentTerm = mortgageData.loanTerm;
  const recommendedTerm = currentTerm;
  
  let rationale = '';
  if (currentTerm === 30) {
    rationale = 'Lower monthly payments provide maximum flexibility. Consider 15-year if you can afford higher payments to save significantly on interest.';
  } else if (currentTerm === 15) {
    rationale = 'Excellent choice for minimizing interest costs. You\'ll build equity rapidly and own your home sooner.';
  } else {
    rationale = 'Balanced approach between payment affordability and interest savings.';
  }

  return {
    termComparison,
    recommendedTerm,
    rationale
  };
};
